import { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/DataTable";
import { UserWithGroup } from "@/generated";
import { Switch } from "@/components/shadcn/switch";
import { Badge } from "@/components/shadcn/badge";
import { useSpotUser } from "@/utils/spotUser";
import { useCallback } from "react";
import { NoxusTooltip } from "../UI/NoxusTooltip";

interface AppUsersTableProps {
  data: UserWithGroup[];
  isLoading?: boolean;
  userAccess: Record<string, boolean>;
  onUserAccessChange: (userAccess: Record<string, boolean>) => void;
}

export const AppUsersTable = ({
  data,
  isLoading,
  userAccess,
  onUserAccessChange,
}: AppUsersTableProps) => {
  const { spotGroup } = useSpotUser();
  const currentGroupId = spotGroup.id;

  const handleAccessToggle = (userId: string, hasAccess: boolean) => {
    onUserAccessChange({
      ...userAccess,
      [userId]: hasAccess,
    });
  };

  const getUserRoleInCurrentGroup = useCallback(
    (userWithGroup: UserWithGroup): string | null => {
      const currentGroup = userWithGroup.groups?.find(
        (group) => group.id === currentGroupId
      );
      return currentGroup?.role || null;
    },
    [currentGroupId]
  );

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "admin":
      case "tenant_admin":
        return "default";
      case "editor":
        return "secondary";
      default:
        return "outline";
    }
  };

  const shouldShowRoleBadge = (role: string | null): boolean => {
    return role === "admin" || role === "tenant_admin" || role === "editor";
  };

  const columns: ColumnDef<UserWithGroup, any>[] = [
    {
      id: "display_name",
      accessorKey: "display_name",
      cell: ({ row }) => {
        const role = getUserRoleInCurrentGroup(row.original);
        return (
          <div className="flex items-center gap-2">
            <div className="text-sm truncate max-w-[175px]">
              {row.original.user.display_name}
            </div>
            {role && shouldShowRoleBadge(role) && (
              <Badge variant={getRoleBadgeVariant(role)} className="text-xs">
                {role === "tenant_admin" ? "admin" : role}
              </Badge>
            )}
          </div>
        );
      },
      header: "Name",
      meta: {
        width: "40%",
      },
    },
    {
      id: "email",
      accessorKey: "email",
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground truncate max-w-[300px]">
          {row.original.user.email}
        </div>
      ),
      header: "Email",
      meta: {
        width: "40%",
      },
    },
    {
      id: "has_access",
      cell: ({ row }) => {
        const role = getUserRoleInCurrentGroup(row.original);

        const always_allow = role === "admin" || role === "editor";
        const accessLabel = `Access for ${row.original.user.email}`;
        if (always_allow) {
          return (
            <div className="flex justify-end">
              <NoxusTooltip content="This user has access to this app by default">
                <Switch aria-label={accessLabel} checked={true} disabled />
              </NoxusTooltip>
            </div>
          );
        }
        return (
          <div className="flex justify-end">
            <Switch
              aria-label={accessLabel}
              checked={userAccess[row.original.user.id] || false}
              onCheckedChange={(checked) =>
                handleAccessToggle(row.original.user.id, checked)
              }
            />
          </div>
        );
      },
      header: "Has access",
      meta: {
        width: "10%",
      },
    },
  ];

  return (
    <DataTable
      data={data}
      columns={columns}
      isLoading={isLoading}
      noDataMessage="No users found"
      loadingRows={10}
      tableLayout="fixed"
    />
  );
};
